import React from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import RAJUKComplianceChecker from './components/RAJUKComplianceChecker';
import BuildingApprovalStatus from './components/BuildingApprovalStatus';
import LandOwnershipValidation from './components/LandOwnershipValidation';
import PropertyVerification from './components/PropertyVerification';
import RAJUKComplianceTimeline from './components/RAJUKComplianceTimeline';
import BangladeshLegalGuide from './components/BangladeshLegalGuide';

const stats = [
  { value: '100%', label: 'RAJUK Integrated', icon: 'Database' },
  { value: '1,800+', label: 'Projects Tracked', icon: 'Building2' },
  { value: 'Real-Time', label: 'RAJUK Updates', icon: 'Zap' },
  { value: '6', label: 'Approved Banks', icon: 'Lock' }
];

const RAJUKComplianceIntelligence = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #0A1628 0%, #0D1F3C 50%, #0A1628 100%)' }}>
      {/* Header */}
      <div className="sticky top-0 z-50 px-4 py-3" style={{ background: 'rgba(10,22,40,0.95)', borderBottom: '1px solid rgba(0,180,80,0.15)', backdropFilter: 'blur(12px)' }}>
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <button onClick={() => navigate('/')} className="flex items-center gap-2 text-sm" style={{ color: '#6B9EC4' }}>
            <Icon name="ArrowLeft" size={16} color="#6B9EC4" />
            Back to Home
          </button>
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full animate-pulse" style={{ background: '#00B450' }} />
            <span className="text-xs font-medium" style={{ color: '#00B450' }}>RAJUK LIVE</span>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-8 md:py-12">
        {/* Hero */}
        <div className="text-center mb-10 md:mb-14">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium mb-4" style={{ background: 'rgba(0,180,80,0.1)', border: '1px solid rgba(0,180,80,0.3)', color: '#00B450' }}>
            <span>🇧🇩</span>
            <span>RAJUK COMPLIANCE INTELLIGENCE</span>
          </div>
          <h1 className="text-2xl md:text-4xl lg:text-5xl font-bold mb-4" style={{ fontFamily: "'Orbitron', sans-serif", color: '#E8F4FF' }}>
            <span style={{ color: '#00B450', textShadow: '0 0 30px rgba(0,180,80,0.4)' }}>রাজধানী উন্নয়ন কর্তৃপক্ষ</span>
            <br />
            <span>& Bangladesh Property Compliance</span>
          </h1>
          <p className="text-base md:text-lg max-w-3xl mx-auto" style={{ color: '#6B9EC4' }}>
            Full integration with RAJUK and CDA systems. Instant compliance verification, building permit checks, developer registration status, and land ownership validation for every Bangladesh property in Dhaka and Chittagong.
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
          {stats?.map((s, i) => (
            <div key={i} className="text-center p-4 rounded-xl" style={{ background: 'rgba(10,22,40,0.8)', border: '1px solid rgba(0,180,80,0.2)' }}>
              <div className="w-8 h-8 rounded-lg flex items-center justify-center mx-auto mb-2" style={{ background: 'rgba(0,180,80,0.1)' }}>
                <Icon name={s?.icon} size={16} color="#00B450" />
              </div>
              <div className="text-xl md:text-2xl font-bold" style={{ fontFamily: "'JetBrains Mono', monospace", color: '#00B450' }}>{s?.value}</div>
              <div className="text-xs" style={{ color: '#6B9EC4' }}>{s?.label}</div>
            </div>
          ))}
        </div>

        {/* City Coverage Banner */}
        <div className="rounded-2xl p-4 mb-8 flex flex-wrap items-center gap-4" style={{ background: 'rgba(0,180,80,0.06)', border: '1px solid rgba(0,180,80,0.2)' }}>
          <div className="flex items-center gap-2">
            <span className="text-lg">🏙️</span>
            <div>
              <div className="text-sm font-bold" style={{ color: '#E8F4FF' }}>Dhaka Division</div>
              <div className="text-xs" style={{ color: '#6B9EC4' }}>RAJUK jurisdiction — 40+ thanas</div>
            </div>
          </div>
          <div className="w-px h-8 hidden md:block" style={{ background: 'rgba(0,180,80,0.3)' }} />
          <div className="flex items-center gap-2">
            <span className="text-lg">⚓</span>
            <div>
              <div className="text-sm font-bold" style={{ color: '#E8F4FF' }}>Chittagong Division</div>
              <div className="text-xs" style={{ color: '#6B9EC4' }}>CDA jurisdiction — port city coverage</div>
            </div>
          </div>
          <div className="w-px h-8 hidden md:block" style={{ background: 'rgba(0,180,80,0.3)' }} />
          <div className="flex items-center gap-2">
            <Icon name="ShieldCheck" size={18} color="#00B450" />
            <div>
              <div className="text-sm font-bold" style={{ color: '#E8F4FF' }}>REHAB Verified</div>
              <div className="text-xs" style={{ color: '#6B9EC4' }}>Real Estate & Housing Association of Bangladesh</div>
            </div>
          </div>
          <div className="ml-auto">
            <button
              onClick={() => window.open('http://www.rajuk.gov.bd', '_blank', 'noopener,noreferrer')}
              className="px-4 py-2 rounded-lg text-xs font-semibold"
              style={{ background: 'rgba(0,180,80,0.15)', border: '1px solid rgba(0,180,80,0.4)', color: '#00B450' }}
            >
              Visit RAJUK Portal →
            </button>
          </div>
        </div>

        {/* Main Compliance Checker */}
        <div className="mb-8">
          <RAJUKComplianceChecker />
        </div>

        {/* Two Column Grid — Building Approval + Land Ownership */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <BuildingApprovalStatus />
          <LandOwnershipValidation />
        </div>

        {/* Property Verification — Dhaka & Chittagong */}
        <div className="mb-8">
          <PropertyVerification />
        </div>

        {/* Timeline + Legal */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <RAJUKComplianceTimeline />
          <BangladeshLegalGuide />
        </div>

        {/* Quick Actions */}
        <div className="rounded-2xl p-6 md:p-8" style={{ background: 'rgba(10,22,40,0.9)', border: '1px solid rgba(0,180,80,0.2)' }}>
          <h3 className="text-lg font-bold mb-4" style={{ color: '#E8F4FF' }}>Quick Actions</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { label: 'RAJUK Complaint', icon: 'AlertCircle', color: '#ef4444' },
              { label: 'Building Permit Check', icon: 'FileText', color: '#00B450' },
              { label: 'Land Record Audit', icon: 'BarChart2', color: '#22c55e' },
              { label: 'Developer Verification', icon: 'UserCheck', color: '#f59e0b' }
            ]?.map((action, i) => (
              <button
                key={i}
                className="flex flex-col items-center gap-2 p-4 rounded-xl transition-all"
                style={{ background: 'rgba(0,180,80,0.05)', border: '1px solid rgba(0,180,80,0.15)' }}
                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(0,180,80,0.12)'; e.currentTarget.style.borderColor = 'rgba(0,180,80,0.4)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'rgba(0,180,80,0.05)'; e.currentTarget.style.borderColor = 'rgba(0,180,80,0.15)'; }}
              >
                <Icon name={action?.icon} size={20} color={action?.color} />
                <span className="text-xs text-center font-medium" style={{ color: '#E8F4FF' }}>{action?.label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RAJUKComplianceIntelligence;
